import { BusinessObjectBase } from './base';
import { horseSchema } from '@schemas/business-objects/horse';
import type { GroupMatch } from './group-match';
import type { HorseGroupMember } from './horse-group-member';
import type { Match } from './match';
import type { Currency, HorseName, Id, Inches, Sex } from '../base-types';

export interface HorseData {
  readonly id: Id;
  readonly name: HorseName;
  readonly sex: Sex;
  readonly phenotype?: string;
  readonly sireName?: HorseName;
  readonly owner?: string;
  readonly yearOfBirth?: number;
  readonly bmeScore?: number;
  readonly studFee?: Currency;
  readonly state?: string;
  readonly country?: string;
  readonly active?: boolean;
  readonly height?: Inches;
  readonly heartGirth?: Inches;
  readonly length?: Inches;
  readonly flank?: Inches;
  readonly frontLeg?: Inches;
  readonly backPastern?: Inches;
  readonly frontPastern?: Inches;
  readonly back?: Inches;
  readonly hipToHock?: Inches;
  readonly hockToGround?: Inches;
  readonly ilium?: Inches;
  readonly tuberCalcis?: Inches;
  readonly gaskin?: Inches;
  readonly tibia?: Inches;
  readonly femur?: Inches;
  readonly bodyLength?: Inches;
  readonly legacyId?: number;
  readonly groupMemberships?: HorseGroupMember[];
  readonly matchesAsMare?: Match[];
  readonly matchesAsStallion?: Match[];
  readonly groupMatches?: GroupMatch[];
}

export interface Horse extends Readonly<HorseData> {}
type HorseCreateWithChildrenData = Partial<HorseData>;

export class Horse extends BusinessObjectBase {
  static readonly entityName = 'Horse' as const;
  static readonly plural = 'horses';
  static schema = horseSchema;

  // --- Static CRUD methods ---

  static async find(
    filter?: any,
    options?: { quietErrors?: boolean },
  ): Promise<Horse[]> {
    const url = `${this.baseUrl}/${this.plural}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const results = await this.get<any[]>(url, params, undefined, options);
    return results.map((data) => new Horse(data));
  }

  static async findOne(
    filter?: any,
    options?: { quietErrors?: boolean },
  ): Promise<Horse | null> {
    const url = `${this.baseUrl}/${this.plural}/find-one`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params, undefined, options);
    return data ? new Horse(data) : null;
  }

  static async findById(
    id: Id,
    filter?: any,
    options?: { quietErrors?: boolean },
  ): Promise<Horse> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params, undefined, options);
    return new Horse(data);
  }

  static async create(data: Omit<HorseData, 'id'>): Promise<Horse> {
    const url = `${this.baseUrl}/${this.plural}`;
    const result = await this.post<any>(url, data);
    return new Horse(result);
  }

  static async createMany(data: Omit<HorseData, 'id'>[]): Promise<Horse[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new Horse(item));
  }

  static async createWithChildren(
    data: HorseCreateWithChildrenData,
  ): Promise<Horse> {
    const url = `${this.baseUrl}/${this.plural}/create-with-children`;
    const result = await this.post<any>(url, data);
    return new Horse(result);
  }

  static async createManyWithChildren(
    data: HorseCreateWithChildrenData[],
  ): Promise<Horse[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many-with-children`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new Horse(item));
  }

  static async updateById(id: Id, updates: Partial<HorseData>): Promise<Horse> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const result = await this.patch<any>(url, updates);
    return new Horse(result);
  }

  static async deleteById(id: Id): Promise<boolean> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    try {
      await this.del<void>(url);
      return true;
    } catch {
      return false;
    }
  }

  // --- Behaviors ---

  static async countMatching(where?: Record<string, any>): Promise<number> {
    const url = `${this.baseUrl}/${this.plural}/count-matching`;
    return this.post<number>(url, { where });
  }
}
